// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Custom hook for managing selection data

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useFacility } from '@/contexts/FacilityContext';
import { Selection, EnhancedSelection } from '@/types/selection';
import { PlantTraitFormData } from '@/components/PlantTraitDialog';
import { PhenoSensory } from '@/types/sensory';
import { transformToEnhancedSelection } from '@/utils/selectionUtils';
import { useToast } from '@/hooks/use-toast';

export function useSelections(projectId?: string) {
  const { activeFacility } = useFacility();
  const [selections, setSelections] = useState<Selection[]>([]);
  const [enhancedSelections, setEnhancedSelections] = useState<EnhancedSelection[]>([]);
  const [plants, setPlants] = useState<PlantTraitFormData[]>([]);
  const [sensoryData, setSensoryData] = useState<Record<string, PhenoSensory>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch selections for a project
   */
  const fetchSelections = async (projectId: string) => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch selections
      const { data: selectionsData, error: selectionsError } = await supabase
        .from('selections')
        .select('*')
        .eq('project_id', projectId);
      
      if (selectionsError) throw selectionsError;
      
      // Fetch plants
      const { data: plantsData, error: plantsError } = await supabase
        .from('phenos')
        .select('*')
        .eq('project_id', projectId);
      
      if (plantsError) throw plantsError;
      
      // Transform plants data to PlantTraitFormData format
      const transformedPlants = plantsData.map((plant: any) => ({
        id: plant.id,
        projectId: plant.project_id,
        name: plant.plant_id,
        status: plant.status || 'Active',
        notes: plant.notes || '',
        imageUrl: plant.images?.[0] || undefined,
        growthStage: plant.growth_stage || 'Vegetation',
        dateAdded: plant.created_at,
        lastUpdated: plant.updated_at,
        traits: {
          vigor: plant.growth_rate || 0,
          structure: plant.structure || 0,
          aroma: plant.aroma_intensity || 0,
          frost: plant.frost_production || 0,
          yield: plant.yield_estimate || 0,
          overall: plant.potency_estimate || 0,
          color: 0,
          budDensity: plant.bud_density || 0
        },
        customTraits: [],
        measurements: {
          height: plant.height,
          internodalSpacing: plant.internodal,
          floweringTime: plant.flowering_time,
          branchingDensity: plant.branching_density,
          leafSize: plant.leaf_size,
          leafWidth: plant.leaf_width
        },
        selectedForFinal: plant.selected_for_final || false,
        selectionNotes: plant.selection_notes || '',
        aromaProfile: plant.aroma_profile || []
      }));
      
      // Fetch sensory data for all plants
      const plantIds = transformedPlants.map(plant => plant.name);
      
      if (plantIds.length > 0) {
        const { data: sensoryDataResult, error: sensoryError } = await supabase
          .from('phenos_sensory' as any)
          .select('*')
          .in('plant_id', plantIds);
        
        if (sensoryError && sensoryError.code !== 'PGRST116') {
          console.error('Error fetching sensory data:', sensoryError);
        } else if (sensoryDataResult) {
          // Create a map of plant_id to sensory data
          const sensoryMap: Record<string, PhenoSensory> = {};
          
          sensoryDataResult.forEach((data: any) => {
            sensoryMap[data.plant_id] = data as PhenoSensory;
          });
          
          setSensoryData(sensoryMap);
        }
      }
      
      // Set state
      setSelections(selectionsData);
      setPlants(transformedPlants);
      
      // Create enhanced selections
      const enhanced = selectionsData.map((selection: Selection) => {
        const plant = transformedPlants.find(p => p.id === selection.phenotype_id);
        const sensory = plant ? sensoryData[plant.name] : undefined;
        
        if (!plant) {
          console.warn(`Plant not found for selection ${selection.id}`);
          return null;
        }
        
        return transformToEnhancedSelection(selection, plant, sensory);
      }).filter(Boolean) as EnhancedSelection[];
      
      setEnhancedSelections(enhanced);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch selections';
      setError(errorMessage);
      console.error('Error fetching selections:', err);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new selection
   */
  const createSelection = async (
    phenotypeId: string,
    plantId: string,
    data: {
      generation: string;
      reason: string;
      traitsSelected: string[];
      selectionNotes?: string;
    }
  ) => {
    if (!projectId) {
      setError('Project ID is required');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: result, error } = await supabase
        .from('selections')
        .insert({
          project_id: projectId,
          phenotype_id: phenotypeId,
          selection_date: new Date().toISOString(),
          generation: data.generation,
          reason: data.reason,
          traits_selected: data.traitsSelected,
          user_id: user?.id,
          facility_id: activeFacility?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Update the plant's selection notes if provided
      if (data.selectionNotes) {
        const { error: updateError } = await supabase
          .from('phenos')
          .update({
            selection_notes: data.selectionNotes,
            selected_for_final: true
          })
          .eq('id', phenotypeId);
        
        if (updateError) {
          console.error('Error updating plant selection notes:', updateError);
        }
      }
      
      // Refresh selections
      await fetchSelections(projectId);
      
      toast({
        title: 'Success',
        description: 'Selection created successfully',
      });
      
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create selection';
      setError(errorMessage);
      console.error('Error creating selection:', err);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing selection
   */
  const updateSelection = async (
    selectionId: string,
    data: {
      generation?: string;
      reason?: string;
      traitsSelected?: string[];
      selectionNotes?: string;
    }
  ) => {
    if (!projectId) {
      setError('Project ID is required');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Find the selection to update
      const selection = selections.find(s => s.id === selectionId);
      
      if (!selection) {
        throw new Error('Selection not found');
      }
      
      // Update the selection
      const { data: result, error } = await supabase
        .from('selections')
        .update({
          generation: data.generation || selection.generation,
          reason: data.reason || selection.reason,
          traits_selected: data.traitsSelected || selection.traits_selected,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectionId)
        .select()
        .single();
      
      if (error) throw error;
      
      // Update the plant's selection notes if provided
      if (data.selectionNotes) {
        const { error: updateError } = await supabase
          .from('phenos')
          .update({
            selection_notes: data.selectionNotes
          })
          .eq('id', selection.phenotype_id);
        
        if (updateError) {
          console.error('Error updating plant selection notes:', updateError);
        }
      }
      
      // Refresh selections
      await fetchSelections(projectId);
      
      toast({
        title: 'Success',
        description: 'Selection updated successfully',
      });
      
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update selection';
      setError(errorMessage);
      console.error('Error updating selection:', err);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a selection
   */
  const deleteSelection = async (selectionId: string) => {
    if (!projectId) {
      setError('Project ID is required');
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Find the selection to delete
      const selection = selections.find(s => s.id === selectionId);
      
      if (!selection) {
        throw new Error('Selection not found');
      }
      
      // Delete the selection
      const { error } = await supabase
        .from('selections')
        .delete()
        .eq('id', selectionId);
      
      if (error) throw error;
      
      // Refresh selections
      await fetchSelections(projectId);
      
      toast({
        title: 'Success',
        description: 'Selection deleted successfully',
      });
      
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete selection';
      setError(errorMessage);
      console.error('Error deleting selection:', err);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch selections when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchSelections(projectId);
    }
  }, [projectId]);

  return {
    selections,
    enhancedSelections,
    plants,
    sensoryData,
    isLoading,
    error,
    fetchSelections,
    createSelection,
    updateSelection,
    deleteSelection
  };
} 